/**
 * The one place that knows who is logged in.
 *
 * `App` reads `status` to decide what to render before anything else does —
 * the sidebar's status poll, every lazy page, all of it assume a session
 * already exists, so nothing downstream re-checks. Three explicit states plus
 * loading, not a boolean: "no session" and "couldn't even ask" look the same
 * on screen (nothing loads) but need different UI (a login form vs. an error
 * card with retry), and collapsing them would either show a login form for a
 * dead backend or an error card for someone who is simply logged out.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  changePassword as apiChangePassword,
  getMe,
  login as apiLogin,
  logout as apiLogout,
  setUnauthenticatedHandler,
} from "../lib/api";
import { describeError } from "./useApi";
import { clearDayCache } from "../lib/dayCache";
import type { AuthUser } from "../types";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "error";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Ukrainian, user-facing. Only set when `status === "error"`. */
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Re-run the `/api/auth/me` probe — the retry button on the error card. */
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkSession = useCallback(() => {
    setStatus("loading");
    setError(null);
    getMe()
      .then((me) => {
        setUser(me);
        setStatus("authenticated");
      })
      .catch((err: unknown) => {
        const described = describeError(err);
        if (described.code === "unauthenticated") {
          setUser(null);
          setStatus("unauthenticated");
        } else {
          // The backend may simply not be running yet — not the same thing
          // as "nobody is logged in", and showing a login form for it would
          // send a correct password into a wall for no reason.
          setError(described.message);
          setStatus("error");
        }
      });
  }, []);

  useEffect(checkSession, [checkSession]);

  // Any other endpoint's 401 lands here — one place decides the session is
  // over, instead of every page catching it separately. Registered only while
  // this provider is mounted, which in practice is the whole app lifetime.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      clearDayCache();
      setUser(null);
      setStatus("unauthenticated");
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    // Nothing cached in the tab belongs to the account signing in — and this
    // provider never reloads the page, so nothing is dropped for us.
    clearDayCache();
    const me = await apiLogin({ email, password });
    setUser(me);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      // The cookie may already be gone server-side (expired, another tab) —
      // the screen leaves either way, which is the whole point of the button.
      // The calendar he was looking at goes with it: logging out is React
      // state, not a reload, so an in-memory cache would survive him.
      clearDayCache();
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await apiChangePassword({ current_password: currentPassword, new_password: newPassword });
  }, []);

  return (
    <AuthContext value={{ status, user, error, login, logout, changePassword, retry: checkSession }}>
      {children}
    </AuthContext>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
